import * as React from 'react';
import { DocumentReader } from '../../word/api/DocumentReader';
import { TrackChangesExtractor } from '../../word/utils/TrackChangesExtractor';
import apiService from '../../services/api';
import { DocumentReviewResults } from './DocumentReviewResults';
import './ReviewView.css';

type Step = 'loading' | 'question1a' | 'question1b' | 'question2' | 'reviewing' | 'reviewResults';

// Review findings types
interface ReviewFinding {
  paragraphIndex: number;
  explanation: {
    ourSideInterest: string;
    theirSideInterest: string;
    whyCritical?: string;
    whyWarning?: string;
    whyInfo?: string;
  };
}

interface ReviewFindings {
  critical: ReviewFinding[];
  warning: ReviewFinding[];
  info: ReviewFinding[];
}

export interface ReviewViewProps {
  /**
   * Called after party identification is persisted to localStorage (our side has at least one entity).
   * Parent may switch tabs (e.g. return user to Ask/Agent/Editor after a gated review flow).
   */
  onReviewContextCommitted?: () => void;
}

export const ReviewView: React.FC<ReviewViewProps> = ({ onReviewContextCommitted }) => {
  const [step, setStep] = React.useState<Step>('loading');
  const [entities, setEntities] = React.useState<string[]>([]);
  const [uniqueAuthors, setUniqueAuthors] = React.useState<string[]>([]);
  const [hasRedlines, setHasRedlines] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [subProjectId, setSubProjectId] = React.useState<string | null>(null);

  // Question 1 state
  const [selectedOurSide, setSelectedOurSide] = React.useState<string[]>([]);
  const [selectedTheirSide, setSelectedTheirSide] = React.useState<string[]>([]);
  const [optionalDetails, setOptionalDetails] = React.useState<string>('');

  // Question 2 state
  const [sideAssignments, setSideAssignments] = React.useState<Record<string, 'our_side' | 'their_side'>>({});
  const [isIdentifyingSides, setIsIdentifyingSides] = React.useState<boolean>(false);

  // Document review state
  const [reviewFindings, setReviewFindings] = React.useState<ReviewFindings | null>(null);
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set(['critical', 'warning', 'info']));
  const [documentParagraphs, setDocumentParagraphs] = React.useState<Array<{ index: number; text: string }>>([]);

  // Load document content and extract entities on mount
  React.useEffect(() => {
    // First, try to restore saved state
    const savedState = loadSavedState();
    if (savedState && savedState.reviewFindings && savedState.documentParagraphs) {
      // We have complete review results, restore them
      setSelectedOurSide(savedState.ourSide || []);
      setSelectedTheirSide(savedState.theirSide || []);
      setOptionalDetails(savedState.optionalDetails || '');
      setHasRedlines(savedState.hasRedlines || false);
      setSideAssignments(savedState.sideAssignments || {});
      setReviewFindings(savedState.reviewFindings);
      setDocumentParagraphs(savedState.documentParagraphs);
      setStep('reviewResults');
      console.log('Restored saved review state');
    } else {
      // No saved state or incomplete, start fresh
      loadDocumentData();
    }
    // Try to get subProjectId from localStorage or context
    checkForSubProject();
  }, []);

  const checkForSubProject = () => {
    // Check if there's a sub-project ID in localStorage (set by other components)
    try {
      const stored = localStorage.getItem('selectedSubProjectId');
      if (stored) {
        setSubProjectId(stored);
      }
    } catch (err) {
      console.error('Error checking for sub-project:', err);
    }
  };

  const loadDocumentData = async () => {
    try {
      setError(null);

      // Load independent document inputs in parallel to reduce first-load latency
      const [firstPageText, ooxml] = await Promise.all([
        DocumentReader.getFirstPageText(),
        DocumentReader.getFullDocumentOOXML(),
      ]);

      // Extract entities from first page text via engine
      if (firstPageText && firstPageText.length > 0) {
        try {
          const extractedEntities = await apiService.extractEntities(firstPageText);
          setEntities(extractedEntities);
        } catch (err) {
          console.error('Error extracting entities:', err);
          // Continue without entities
        }
      }

      // Check for track changes to determine if Question 2 is needed
      if (ooxml && ooxml.length > 0) {
        const changes = TrackChangesExtractor.extractTrackChanges(ooxml);
        if (changes.length > 0) {
          setHasRedlines(true);
        }
      }

      // Move to Question 1a (Who are you?)
      setStep('question1a');
    } catch (err: any) {
      console.error('Error loading document data:', err);
      setError(err.message || 'Failed to load document data');
      setStep('question1a'); // Still allow user to proceed
    }
  };

  const toggleOurSide = (entity: string) => {
    setSelectedOurSide(prev => {
      if (prev.includes(entity)) {
        return prev.filter(e => e !== entity);
      } else {
        return [...prev, entity];
      }
    });
  };

  const toggleTheirSide = (entity: string) => {
    setSelectedTheirSide(prev => {
      if (prev.includes(entity)) {
        return prev.filter(e => e !== entity);
      } else {
        return [...prev, entity];
      }
    });
  };

  const handleQuestion1aSubmit = () => {
    if (selectedOurSide.length === 0) {
      setError('Please select or enter at least one entity for your side');
      return;
    }

    setError(null);
    // Move to Question 1b (Who is the other side?)
    setStep('question1b');
  };

  const handleQuestion1bSubmit = async () => {
    if (selectedTheirSide.length === 0) {
      setError('Please select or enter at least one entity for the other side');
      return;
    }

    setError(null);

    // If there are redlines, move to Question 2
    if (hasRedlines) {
      setStep('question2');
      // Load authors from identify sides API if subProjectId is available
      if (subProjectId) {
        await loadAuthorsFromAPI();
      } else {
        // Fallback: extract authors from track changes locally
        await loadAuthorsFromTrackChanges();
      }
    } else {
      // No redlines, start document review directly
      saveResults();
      notifyReviewContextCommittedIfReady();
      await startDocumentReview();
    }
  };

  const loadAuthorsFromAPI = async () => {
    if (!subProjectId) return;

    try {
      setIsIdentifyingSides(true);
      const authors = await apiService.identifySides(subProjectId);
      setUniqueAuthors(authors);
      
      // Try to load existing side assignments
      try {
        const existingAssignments = await apiService.getSideAssignments(subProjectId);
        if (Object.keys(existingAssignments).length > 0) {
          setSideAssignments(existingAssignments);
        }
      } catch (err) {
        console.error('Error loading existing side assignments:', err);
      }
    } catch (err: any) {
      console.error('Error identifying sides from API:', err);
      // Fallback to local extraction
      await loadAuthorsFromTrackChanges();
    } finally {
      setIsIdentifyingSides(false);
    }
  };

  const loadAuthorsFromTrackChanges = async () => {
    try {
      const ooxml = await DocumentReader.getFullDocumentOOXML();
      if (ooxml && ooxml.length > 0) {
        const changes = TrackChangesExtractor.extractTrackChanges(ooxml);
        const authors = Array.from(new Set(changes.map(c => c.author)));
        setUniqueAuthors(authors);
      }
    } catch (err) {
      console.error('Error extracting authors from track changes:', err);
    }
  };

  const handleQuestion2Submit = async () => {
    // Validate all authors are assigned
    const allAssigned = uniqueAuthors.every(author => sideAssignments[author]);
    
    if (!allAssigned) {
      setError('Please assign all authors to sides');
      return;
    }

    setError(null);

    // Save side assignments if subProjectId is available
    if (subProjectId) {
      try {
        await apiService.saveSideAssignments(subProjectId, sideAssignments);
      } catch (err) {
        console.error('Error saving side assignments:', err);
        // Continue anyway
      }
    }

    saveResults();
    notifyReviewContextCommittedIfReady();
    // Start document review instead of showing complete
    await startDocumentReview();
  };

  const startDocumentReview = async () => {
    try {
      const startedAt = Date.now();
      setStep('reviewing');
      setError(null);

      // Use shared Ask/Review context pipeline (single optimized read path)
      const contextStart = Date.now();
      const { fullText, paragraphs } = await DocumentReader.getFullDocumentContext();
      const contextMs = Date.now() - contextStart;
      
      // Store paragraphs for later use in rendering findings
      setDocumentParagraphs(paragraphs);

      // Prepare review context
      const reviewContext = {
        ourSide: selectedOurSide,
        theirSide: selectedTheirSide,
        sideAssignments: hasRedlines ? sideAssignments : undefined,
        optionalDetails: optionalDetails || undefined,
      };

      // Call API to review document
      const apiStart = Date.now();
      const findings = await apiService.reviewDocument(fullText, paragraphs, reviewContext);
      const apiMs = Date.now() - apiStart;
      console.log('[Perf][ReviewFlow] review_document_done', {
        contextMs,
        apiMs,
        totalMs: Date.now() - startedAt,
        fullTextLength: fullText.length,
        paragraphsCount: paragraphs.length,
        ourSideCount: reviewContext.ourSide.length,
        theirSideCount: reviewContext.theirSide.length,
        sideAssignmentsCount: reviewContext.sideAssignments ? Object.keys(reviewContext.sideAssignments).length : 0,
        critical: findings?.critical?.length ?? 0,
        warning: findings?.warning?.length ?? 0,
        info: findings?.info?.length ?? 0,
      });
      
      setReviewFindings(findings);
      setDocumentParagraphs(paragraphs);
      setStep('reviewResults');
      
      // Save complete state after review is done
      const completeState = {
        ourSide: selectedOurSide,
        theirSide: selectedTheirSide,
        optionalDetails,
        hasRedlines,
        sideAssignments: hasRedlines ? sideAssignments : {},
        reviewFindings: findings,
        documentParagraphs: paragraphs,
        timestamp: new Date().toISOString(),
      };
      
      try {
        localStorage.setItem('courtyard_review_complete_state', JSON.stringify(completeState));
        console.log('Complete review state saved');
      } catch (err) {
        console.error('Error saving complete state:', err);
      }
    } catch (err: any) {
      console.error('Error reviewing document:', err);
      setError(err.message || 'Failed to review document');
      setStep('reviewResults'); // Still show results even if empty
    }
  };


  const saveResults = () => {
    const results = {
      ourSide: selectedOurSide,
      theirSide: selectedTheirSide,
      optionalDetails,
      hasRedlines,
      sideAssignments: hasRedlines ? sideAssignments : {},
      timestamp: new Date().toISOString(),
    };

    try {
      localStorage.setItem('courtyard_review_results', JSON.stringify(results));
      console.log('Review results saved:', results);
    } catch (err) {
      console.error('Error saving results:', err);
    }
  };

  /** Same as legacy `onSidesIdentified`: full submit paths only — not Skip. */
  const notifyReviewContextCommittedIfReady = () => {
    if (selectedOurSide.filter(Boolean).length > 0) {
      onReviewContextCommitted?.();
    }
  };


  const loadSavedState = (): any => {
    try {
      const saved = localStorage.getItem('courtyard_review_complete_state');
      if (saved) {
        const state = JSON.parse(saved);
        // Check if state is recent (within last 24 hours) and has required data
        const timestamp = new Date(state.timestamp);
        const now = new Date();
        const hoursDiff = (now.getTime() - timestamp.getTime()) / (1000 * 60 * 60);
        
        if (hoursDiff < 24 && state.reviewFindings && state.documentParagraphs) {
          return state;
        }
      }
    } catch (err) {
      console.error('Error loading saved state:', err);
    }
    return null;
  };

  const addManualEntity = (side: 'our' | 'their', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    
    const entities = trimmed.split(',').map(e => e.trim()).filter(e => e.length > 0);
    
    if (side === 'our') {
      setSelectedOurSide(prev => {
        const newEntities = entities.filter(e => !prev.includes(e));
        return [...prev, ...newEntities];
      });
    } else {
      setSelectedTheirSide(prev => {
        const newEntities = entities.filter(e => !prev.includes(e));
        return [...prev, ...newEntities];
      });
    }
  };

  const handleSkip = async () => {
    if (step === 'question1a') {
      // Skip "Who are you?" - move to "Who is the other side?"
      setStep('question1b');
    } else if (step === 'question1b') {
      // Skip "Who is the other side?" - move to Question 2 if redlines exist, otherwise start review
      if (hasRedlines) {
        setStep('question2');
        if (subProjectId) {
          loadAuthorsFromAPI();
        } else {
          loadAuthorsFromTrackChanges();
        }
      } else {
        saveResults();
        await startDocumentReview();
      }
    } else if (step === 'question2') {
      // Skip Question 2, start document review
      saveResults();
      await startDocumentReview();
    }
  };

  const handleRestart = () => {
    // Clear all state
    setStep('loading');
    setEntities([]);
    setUniqueAuthors([]);
    setHasRedlines(false);
    setError(null);
    setSelectedOurSide([]);
    setSelectedTheirSide([]);
    setOptionalDetails('');
    setSideAssignments({});
    setIsIdentifyingSides(false);
    setReviewFindings(null);
    setExpandedCategories(new Set(['critical', 'warning', 'info']));
    setDocumentParagraphs([]);
    
    // Clear saved state from localStorage
    try {
      localStorage.removeItem('courtyard_review_complete_state');
      localStorage.removeItem('courtyard_review_results');
      console.log('Cleared saved review state');
    } catch (err) {
      console.error('Error clearing saved state:', err);
    }
    
    // Reload document data to start fresh
    loadDocumentData();
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="review-view">
        <div className="review-loading">
          <div className="spinner"></div>
          <span>Loading document...</span>
        </div>
      </div>
    );
  }

  // Question 1a: Who are you?
  if (step === 'question1a') {
    // Filter out entities that are already selected for the other side
    const availableEntities = entities.filter(e => !selectedTheirSide.includes(e));
    
    return (
      <div className="review-view">
        <div className="questions-header">
          <h2 className="questions-title">Questions</h2>
          <span className="questions-progress">1 of {hasRedlines ? '3' : '2'}</span>
        </div>

        <div className="question-container">
          <h3 className="question-text">Who are you?</h3>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="options-list">
            {availableEntities.length > 0 ? (
              <>
                {availableEntities.map((entity, idx) => {
                  const letter = String.fromCharCode(65 + (idx % 26)); // A, B, C, D, etc.
                  const isSelected = selectedOurSide.includes(entity);
                  
                  return (
                    <div
                      key={idx}
                      className={`option-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleOurSide(entity)}
                    >
                      <div className="option-letter">{letter}</div>
                      <div className="option-text">{entity}</div>
                      {isSelected && <div className="option-checkmark">✓</div>}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="options-placeholder">
                <p>No entities found. Please enter manually below.</p>
              </div>
            )}
          </div>

          <div className="manual-input-section">
            <div className="form-group">
              <label htmlFor="our-side">YOUR SIDE</label>
              <input
                id="our-side"
                type="text"
                placeholder="Enter entity names (comma-separated)"
                className="review-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addManualEntity('our', (e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
              />
            </div>
          </div>

          {selectedOurSide.length > 0 && (
            <div className="selected-entity-display">
              <p>Selected ({selectedOurSide.length}):</p>
              <div className="selected-entities-list">
                {selectedOurSide.map((entity, idx) => (
                  <span key={idx} className="selected-entity-tag">
                    {entity}
                    <button
                      className="remove-entity-btn"
                      onClick={() => toggleOurSide(entity)}
                      aria-label={`Remove ${entity}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="questions-footer">
          <button onClick={handleSkip} className="button-skip">
            Skip
          </button>
          <button
            onClick={handleQuestion1aSubmit}
            className="button-continue"
            disabled={selectedOurSide.length === 0}
          >
            Continue
            <span className="button-arrow">→</span>
          </button>
        </div>
      </div>
    );
  }

  // Question 1b: Who is the other side?
  if (step === 'question1b') {
    // Filter out entities that were selected as "our side"
    const availableEntities = entities.filter(e => !selectedOurSide.includes(e));
    
    return (
      <div className="review-view">
        <div className="questions-header">
          <h2 className="questions-title">Questions</h2>
          <span className="questions-progress">2 of {hasRedlines ? '3' : '2'}</span>
        </div>

        <div className="question-container">
          <h3 className="question-text">Who is the other side?</h3>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="options-list">
            {availableEntities.length > 0 ? (
              <>
                {availableEntities.map((entity, idx) => {
                  const letter = String.fromCharCode(65 + (idx % 26)); // A, B, C, D, etc.
                  const isSelected = selectedTheirSide.includes(entity);
                  
                  return (
                    <div
                      key={idx}
                      className={`option-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleTheirSide(entity)}
                    >
                      <div className="option-letter">{letter}</div>
                      <div className="option-text">{entity}</div>
                      {isSelected && <div className="option-checkmark">✓</div>}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="options-placeholder">
                <p>No other entities found. Please enter manually below.</p>
              </div>
            )}
          </div>

          <div className="manual-input-section">
            <div className="form-group">
              <label htmlFor="their-side">OTHER SIDE</label>
              <input
                id="their-side"
                type="text"
                placeholder="Enter entity names (comma-separated)"
                className="review-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addManualEntity('their', (e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
              />
            </div>
          </div>

          {selectedTheirSide.length > 0 && (
            <div className="selected-entity-display">
              <p>Selected ({selectedTheirSide.length}):</p>
              <div className="selected-entities-list">
                {selectedTheirSide.map((entity, idx) => (
                  <span key={idx} className="selected-entity-tag">
                    {entity}
                    <button
                      className="remove-entity-btn"
                      onClick={() => toggleTheirSide(entity)}
                      aria-label={`Remove ${entity}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="optional-details">
            <input
              type="text"
              value={optionalDetails}
              onChange={(e) => setOptionalDetails(e.target.value)}
              placeholder="Add more optional details"
              className="optional-input"
            />
          </div>
        </div>

        <div className="questions-footer">
          <button onClick={handleSkip} className="button-skip">
            Skip
          </button>
          <button
            onClick={handleQuestion1bSubmit}
            className="button-continue"
            disabled={selectedTheirSide.length === 0}
          >
            Continue
            <span className="button-arrow">→</span>
          </button>
        </div>
      </div>
    );
  }

  // Question 2: Identify sides from track change authors
  if (step === 'question2') {
    return (
      <div className="review-view">
        <div className="questions-header">
          <h2 className="questions-title">Questions</h2>
          <span className="questions-progress">3 of 3</span>
        </div>

        <div className="question-container">
          <h3 className="question-text">
            We found {uniqueAuthors.length} author{uniqueAuthors.length !== 1 ? 's' : ''} in track changes. 
            Please assign each author to a side.
          </h3>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {isIdentifyingSides ? (
            <div className="review-loading">
              <div className="spinner"></div>
              <span>Identifying authors...</span>
            </div>
          ) : (
            <div className="options-list">
              {uniqueAuthors.map((author, idx) => {
                const letter = String.fromCharCode(65 + idx);
                const assignment = sideAssignments[author];
                
                return (
                  <div key={author} className="option-item">
                    <div className="option-letter">{letter}</div>
                    <div className="option-text">{author}</div>
                    <div className="assignment-buttons">
                      <button
                        onClick={() => setSideAssignments(prev => ({ ...prev, [author]: 'our_side' }))}
                        className={`assignment-button ${assignment === 'our_side' ? 'active our-side' : ''}`}
                      >
                        Our Side
                      </button>
                      <button
                        onClick={() => setSideAssignments(prev => ({ ...prev, [author]: 'their_side' }))}
                        className={`assignment-button ${assignment === 'their_side' ? 'active their-side' : ''}`}
                      >
                        Their Side
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="questions-footer">
          <button onClick={handleSkip} className="button-skip">
            Skip
          </button>
          <button
            onClick={handleQuestion2Submit}
            disabled={!uniqueAuthors.every(author => sideAssignments[author]) || isIdentifyingSides}
            className="button-continue"
          >
            Continue
            <span className="button-arrow">→</span>
          </button>
        </div>
      </div>
    );
  }

  // Reviewing state
  if (step === 'reviewing') {
    return (
      <div className="review-view">
        <div className="review-loading">
          <div className="spinner"></div>
          <span>Reviewing document...</span>
        </div>
      </div>
    );
  }

  // Review results state
  if (step === 'reviewResults') {
    return (
      <div className="review-view">
        {error && (
          <div className="error-message" style={{ margin: '16px' }}>
            {error}
            <button 
              onClick={startDocumentReview}
              style={{ marginLeft: '12px', padding: '4px 8px', fontSize: '12px' }}
            >
              Retry
            </button>
          </div>
        )}
        <DocumentReviewResults 
          findings={reviewFindings}
          expandedCategories={expandedCategories}
          onToggleCategory={(category) => {
            setExpandedCategories(prev => {
              const newSet = new Set(prev);
              if (newSet.has(category)) {
                newSet.delete(category);
              } else {
                newSet.add(category);
              }
              return newSet;
            });
          }}
          paragraphs={documentParagraphs}
          onRestart={handleRestart}
        />
      </div>
    );
  }

  return null;
};
